import React from "react";
import { classNames } from "primereact/utils";
import { AppTheme } from "../../AppTheme";

type InvoiceStatus =
  | "draft"
  | "sent"
  | "viewed"
  | "partial"
  | "paid"
  | "overdue"
  | "cancelled"
  | "refunded"
  | "active"
  | "pending"
  | "write_off"
  | "deleted";

interface StatusBadgeProps {
  status: InvoiceStatus;
  className?: string;
}

export const StatusBadge: React.FC<StatusBadgeProps> = ({
  status,
  className = "",
}) => {
  // Updated colors with improved contrast ratios for better accessibility.
  // Backgrounds stay pale in both light and dark mode, so the label is given an
  // explicit dark `textHex` applied via inline style — this keeps it readable in
  // dark mode, where broad `.darkTheme .p-card-content *` rules would otherwise
  // repaint the label light-gray and wash it out against the pale badge.
  const statusConfig: Record<
    InvoiceStatus,
    { label: string; bgColor: string; textColor: string; textHex: string }
  > = {
    draft: {
      label: "Draft",
      bgColor: "bg-gray-200",
      textColor: "text-gray-800",
      textHex: "#1f2937",
    },
    sent: {
      label: "Sent",
      bgColor: "bg-amber-200",
      textColor: "text-amber-900",
      textHex: "#78350f",
    },
    viewed: {
      label: "Viewed",
      bgColor: "bg-blue-200",
      textColor: "text-blue-900",
      textHex: "#1e3a8a",
    },
    partial: {
      label: "Partial",
      bgColor: "bg-sky-500",
      textColor: "text-black-900",
      textHex: "#0c2a4d",
    },
    paid: {
      label: "Paid",
      bgColor: "bg-emerald-500",
      textColor: "text-emerald-900",
      textHex: "#064e3b",
    },
    pending: {
      label: "Pending",
      bgColor: "bg-yellow-200",
      textColor: "text-yellow-900",
      textHex: "#713f12",
    },
    overdue: {
      label: "Overdue",
      bgColor: "bg-red-200",
      textColor: "text-red-900",
      textHex: "#7f1d1d",
    },
    cancelled: {
      label: "Cancelled",
      bgColor: "bg-red-200",
      textColor: "text-red-900",
      textHex: "#7f1d1d",
    },
    refunded: {
      label: "Refunded",
      bgColor: "bg-gold",
      textColor: "text-blue-900",
      textHex: "#1e3a8a",
    },
    active: {
      label: "Active",
      bgColor: "bg-teal-200",
      textColor: "text-teal-900",
      textHex: "#134e4a",
    },
    write_off: {
      label: "Write Off",
      bgColor: "bg-teal-200",
      textColor: "text-teal-900",
      textHex: "#134e4a",
    },
    deleted: {
      label: "Deleted",
      bgColor: "bg-red-200",
      textColor: "text-red-900",
      textHex: "#7f1d1d",
    },
  };

  if (!statusConfig[status]) {
    return null; // or some fallback UI
  }

  const { label, bgColor, textColor, textHex } = statusConfig[status];

  const badgeClassName = classNames(
    "inline-flex items-center rounded-full text-xs font-medium",
    bgColor,
    textColor,
    className
  );

  return (
    <span
      className={badgeClassName}
      style={{
        height: "24px",
        padding: "4px 8px",
        borderRadius: "12px",
        color: textHex,
      }}
    >
      {label}
    </span>
  );
};
